import { useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

/** Ordered page paths for number-key navigation (1-9) */
const PAGE_ORDER = [
  "/dashboard",   // 1
  "/brief",       // 2
  "/signals",     // 3
  "/portfolio",   // 4
  "/watchlist",   // 5
  "/research",    // 6
  "/market",      // 7
  "/macro",       // 8
  "/reports",     // 9
];

/** Check if the active element is an input-like element */
function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

interface UseKeyboardShortcutsOptions {
  /** Callback to open the command palette */
  onOpenPalette?: () => void;
}

export function useKeyboardShortcuts(opts: UseKeyboardShortcutsOptions = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Skip if modifier keys held (except for Ctrl+K which is handled in Layout)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Skip if focused on an input element
      if (isInputFocused()) return;

      const key = e.key;

      // Number keys 1-9 for page navigation
      if (key >= "1" && key <= "9") {
        const idx = parseInt(key, 10) - 1;
        if (idx < PAGE_ORDER.length) {
          e.preventDefault();
          navigate(PAGE_ORDER[idx]);
        }
        return;
      }

      // 'r' to refresh current page data
      if (key === "r") {
        e.preventDefault();
        queryClient.invalidateQueries();
        return;
      }

      // '/' to open command palette (vim-style search)
      if (key === "/" && opts.onOpenPalette) {
        e.preventDefault();
        opts.onOpenPalette();
        return;
      }
    },
    [navigate, queryClient, opts],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return { currentPath: location.pathname };
}
